import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, BookOpen, Heart, Pill, FileText, Stethoscope,
  Users, Calendar, ShieldCheck, Brain, ListChecks, Search,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import usePageMeta from "@/hooks/usePageMeta";
import { glossaryJsonLd, guidesItemListJsonLd, breadcrumbJsonLd, SITE_URL } from "@/lib/jsonld";

// ============ GUIDES (long-form-ish bites) ============
const GUIDES = [
  {
    icon: ListChecks,
    title: "Your first 30 days as a caregiver",
    summary: "A gentle starter checklist for when you've just stepped into the role and everything feels urgent at once.",
    bullets: [
      "Make a list of every medication, the dose, and the prescriber",
      "Gather insurance cards, ID, and the most recent doctor's notes",
      "Identify the primary care provider and any specialists currently involved",
      "Ask your loved one (or their existing caregiver) where the legal documents live",
      "Pick one person — sibling, spouse, friend — to be your second pair of eyes",
      "Write down three questions for the next doctor's visit",
    ],
  },
  {
    icon: Pill,
    title: "Organizing medications without losing your mind",
    summary: "Medication mistakes are one of the leading causes of avoidable ER visits. Here's how to set up a system that actually holds.",
    bullets: [
      "Keep a single source of truth — one list, updated every time something changes",
      "Note the purpose of each medication ('for blood pressure', 'for sleep') — you will forget",
      "Photograph each pill bottle so the prescriber, dose, and pharmacy phone number are easy to find",
      "Set reminders at the times your loved one actually takes them — not when it's convenient for you",
      "Bring the full list to every appointment. Doctors only know what you tell them.",
    ],
  },
  {
    icon: Stethoscope,
    title: "How to get more out of every doctor's visit",
    summary: "You usually get 12–15 minutes. Here's how to make them count.",
    bullets: [
      "Write down your top three questions before you go. Three is the right number.",
      "Bring the current medication list (with doses and times)",
      "Note recent symptoms with dates — 'foot swelling on Tuesday and Friday' beats 'sometimes her feet swell'",
      "Ask: 'What should I watch for at home?' and 'When should I call you vs. go to the ER?'",
      "Repeat back the plan in your own words before you leave",
      "Get the visit summary printed or emailed — you won't remember half of it by dinner",
    ],
  },
  {
    icon: Users,
    title: "Talking to family without group-text chaos",
    summary: "Coordinating siblings or extended family is its own job. Here's how to keep everyone in the loop without burning out.",
    bullets: [
      "Pick one channel. Group text + email + phone calls = no one knows what's true.",
      "Send updates on a schedule (e.g., every Sunday evening) instead of every news flash",
      "Be specific about what you need: 'Can someone cover Tuesday?' beats 'I'm overwhelmed.'",
      "Decide who has medical decision-making authority before there's a crisis",
      "If anyone is in denial about the situation, share the doctor's notes — they're harder to argue with",
    ],
  },
  {
    icon: ShieldCheck,
    title: "The legal documents you actually need (and why)",
    summary: "You don't need a lawyer for everything, but you do need the right paperwork before a crisis.",
    bullets: [
      "Healthcare Proxy / Healthcare Power of Attorney — who makes medical decisions when they can't",
      "Durable Power of Attorney (financial) — who handles money and bills",
      "Living Will / Advance Directive — what kind of end-of-life care they want",
      "HIPAA Release — who the doctors are allowed to talk to",
      "Original copies live somewhere safe; copies travel with you. Hospitals need to see them.",
    ],
  },
  {
    icon: Brain,
    title: "When a loved one has memory loss",
    summary: "Caring for someone with dementia, Alzheimer's, or general cognitive decline asks for a different kind of organization.",
    bullets: [
      "Keep familiar routines — predictability reduces anxiety for both of you",
      "Label drawers, cabinets, and rooms with simple words or pictures",
      "Repeat questions are not on purpose. Answer with patience the 14th time the way you did the 1st.",
      "Keep a one-page summary of who they are (career, family, hobbies) for new caregivers and aides",
      "Take photos of the medication routine — they reassure your loved one when you're not there",
      "Build a 'wandering plan' — ID bracelet, neighbors notified, medical alert app",
    ],
  },
];

// ============ GLOSSARY ============
const GLOSSARY = [
  {
    term: "ADL (Activities of Daily Living)",
    def: "The basic self-care tasks: bathing, dressing, eating, toileting, walking, and transferring (moving from bed to chair). When someone needs help with ADLs, they typically need significant in-home or facility-based support. Doctors and insurance companies use this term constantly.",
  },
  {
    term: "IADL (Instrumental Activities of Daily Living)",
    def: "The more complex tasks of independent living: managing medications, cooking, housework, handling finances, transportation, and using the phone. Trouble with IADLs often shows up before trouble with ADLs and is an early signal that someone needs more support.",
  },
  {
    term: "Advance Directive",
    def: "A legal document spelling out what kind of medical care a person wants (and doesn't want) if they become unable to speak for themselves. Includes a living will and may designate a healthcare proxy. Make sure copies are on file with every doctor and hospital they use.",
  },
  {
    term: "Healthcare Proxy / Healthcare Power of Attorney",
    def: "The person legally authorized to make medical decisions for your loved one when they can't. Different from a financial Power of Attorney. Without this document, the hospital decides who they'll talk to — and it may not be you.",
  },
  {
    term: "Durable Power of Attorney (Financial)",
    def: "A legal document that lets a designated person handle financial matters — paying bills, managing accounts, dealing with insurance — on behalf of your loved one. 'Durable' means it stays valid even if your loved one becomes incapacitated.",
  },
  {
    term: "Living Will",
    def: "A specific kind of advance directive that states what end-of-life medical treatments your loved one wants or refuses (CPR, feeding tubes, mechanical ventilation, etc.). Different from a regular will, which deals with property.",
  },
  {
    term: "DNR (Do Not Resuscitate)",
    def: "A medical order — signed by a doctor — that says CPR should not be performed if the heart stops. Usually for patients near end-of-life or with serious illness. It must be on file with the medical team to be honored; a verbal wish isn't enough.",
  },
  {
    term: "POLST / MOLST",
    def: "Physician (or Medical) Orders for Life-Sustaining Treatment. A doctor-signed form that travels with the patient (often bright pink), spelling out specific orders for emergency situations. Used for people with serious illness so EMS and ER teams follow the patient's wishes.",
  },
  {
    term: "HIPAA Release",
    def: "A signed authorization that lets healthcare providers share medical information with people you specify (you, a sibling, a spouse). Without it, doctors and hospitals are often legally barred from talking to you — even if you're the primary caregiver.",
  },
  {
    term: "Care Plan",
    def: "A written summary of your loved one's diagnoses, medications, treatments, providers, and goals of care. Often created with a doctor, social worker, or care manager. Update it any time something changes — it's what aides, family, and ER teams will rely on.",
  },
  {
    term: "Care Coordinator / Care Manager",
    def: "A professional (often a nurse or social worker) who helps families navigate the medical system — finding services, coordinating appointments, advocating with insurance. Some are free through hospitals or insurers; private care managers charge hourly.",
  },
  {
    term: "Home Health vs. Home Care",
    def: "Easy to confuse. 'Home Health' is skilled medical care (nursing visits, physical therapy) usually ordered by a doctor and often covered by Medicare. 'Home Care' is non-medical help (bathing, cooking, companionship) usually paid out of pocket or by long-term care insurance.",
  },
  {
    term: "Respite Care",
    def: "Temporary care — a few hours, a weekend, a week — that gives the primary caregiver a break. Can be in-home, at a day program, or at a residential facility. One of the most under-used resources in caregiving. Use it before you burn out, not after.",
  },
  {
    term: "Hospice",
    def: "Comfort-focused care for people with a terminal illness who are expected to live six months or less. Not the same as 'giving up' — hospice often improves quality of life and is fully covered by Medicare. It can be provided at home, in a facility, or in a dedicated hospice center.",
  },
  {
    term: "Palliative Care",
    def: "Specialized medical care focused on symptom relief and quality of life for people with serious illness — at any stage, alongside curative treatments. Different from hospice (which is end-of-life). Often under-prescribed; ask for it earlier than your doctor may suggest.",
  },
  {
    term: "Skilled Nursing Facility (SNF)",
    def: "A facility that provides 24/7 nursing care, usually short-term after a hospital stay (rehab) or long-term for someone who can't be safely cared for at home. Sometimes called 'rehab' in conversation.",
  },
  {
    term: "Medicare Part A, B, C, D",
    def: "Part A = hospital. Part B = doctor visits and outpatient. Part C = Medicare Advantage (private plans bundling A+B). Part D = prescription drugs. What's covered varies a lot between Medicare and Medicare Advantage — read the specific plan before assuming.",
  },
  {
    term: "Medicaid",
    def: "Different from Medicare. Medicaid is a state-and-federal program for people with limited income/assets that covers long-term care (nursing homes, in-home aides) that Medicare usually doesn't. Eligibility rules vary by state and can be complex; an elder-law attorney can help.",
  },
  {
    term: "Sundowning",
    def: "A pattern seen in dementia where confusion, agitation, or restlessness worsens in late afternoon or evening. Reduce it with consistent routines, daytime activity, lower lighting after dark, and minimizing caffeine. It's not personal — it's the disease.",
  },
  {
    term: "Caregiver Burnout",
    def: "The physical, emotional, and mental exhaustion that comes from sustained caregiving without enough support or breaks. Symptoms: irritability, sleep changes, depression, illness, withdrawal. Not a character flaw — it's a predictable outcome of unsustainable load. The fix is help, not willpower.",
  },
];

export default function Resources() {
  usePageMeta({
    title: "Caregiver Resources & Glossary — Hope Chest",
    description: "Free guides for family caregivers: organizing medications, getting more out of doctor's visits, legal documents you need, and a plain-language glossary of caregiving vocabulary.",
    canonical: `${SITE_URL}/resources`,
    jsonLd: [
      glossaryJsonLd(GLOSSARY),
      guidesItemListJsonLd(GUIDES),
      breadcrumbJsonLd([
        { name: "Home", url: "/" },
        { name: "Resources", url: "/resources" },
      ]),
    ],
  });
  const [query, setQuery] = useState("");

  const q = query.trim().toLowerCase();
  const filteredGlossary = q
    ? GLOSSARY.filter((g) => g.term.toLowerCase().includes(q) || g.def.toLowerCase().includes(q))
    : GLOSSARY;
  const filteredGuides = q
    ? GUIDES.filter((g) => g.title.toLowerCase().includes(q) || g.summary.toLowerCase().includes(q) || g.bullets.some((b) => b.toLowerCase().includes(q)))
    : GUIDES;

  return (
    <div className="min-h-screen bg-[#FCFAF8] text-[#3D3935] relative overflow-hidden">
      <div className="hc-blob" style={{ background: "#E8DCC8", width: 420, height: 420, top: -120, right: -80 }} />
      <div className="hc-blob" style={{ background: "#D8E2D8", width: 360, height: 360, bottom: -100, left: -100 }} />

      <nav className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="resources-home-link">
          <img src="/hope-chest-logo.png" alt="Hope Chest" className="w-10 h-10 object-contain" />
          <span className="font-brand text-xl font-semibold">Hope Chest</span>
        </Link>
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-[#7A736E] hover:text-[#3A5A4A] transition" data-testid="resources-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
      </nav>

      {/* ====== HERO ====== */}
      <header className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 pt-10 pb-12 text-center">
        <BookOpen className="w-10 h-10 text-[#C87B67] mx-auto mb-5" />
        <p className="text-xs uppercase tracking-[0.2em] text-[#7A736E] mb-3">Caregiver resources</p>
        <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.1]" data-testid="resources-title">
          Plain-language guides for family caregivers.
        </h1>
        <p className="mt-6 text-lg text-[#7A736E] max-w-2xl mx-auto">
          No jargon, no medical school required. Just the practical things we wish someone had told us at the start.
        </p>

        {/* Search */}
        <div className="mt-8 relative max-w-md mx-auto">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#7A736E]" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search guides or vocabulary…"
            className="w-full pl-11 pr-4 py-3 rounded-full border border-[#EBE5DF] bg-white focus:border-[#3A5A4A] focus:outline-none text-sm"
            data-testid="resources-search"
          />
        </div>
      </header>

      {/* ====== GUIDES ====== */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 py-12" data-testid="resources-guides-section">
        <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#7A736E] mb-2">Practical guides</p>
            <h2 className="font-display text-3xl md:text-4xl font-semibold">Start here.</h2>
          </div>
          <p className="text-sm text-[#7A736E]">{filteredGuides.length} guide{filteredGuides.length === 1 ? "" : "s"}</p>
        </div>

        {filteredGuides.length === 0 && (
          <p className="text-center text-[#7A736E] py-12">No guides match &quot;{query}&quot;.</p>
        )}

        <div className="grid md:grid-cols-2 gap-5">
          {filteredGuides.map(({ icon: Icon, title, summary, bullets }) => (
            <article key={title} className="hc-card p-7 flex flex-col" data-testid={`guide-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g,'')}`}>
              <div className="flex items-start gap-4 mb-3">
                <div className="w-11 h-11 rounded-xl bg-[#E8DCC8] flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5 text-[#3A5A4A]" />
                </div>
                <h3 className="font-display text-xl leading-tight">{title}</h3>
              </div>
              <p className="text-[15px] text-[#7A736E] leading-relaxed mb-4">{summary}</p>
              <ul className="space-y-2 text-[14.5px] text-[#3D3935]">
                {bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-2.5 leading-relaxed">
                    <span className="text-[#C87B67] mt-1 leading-none">•</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      {/* ====== GLOSSARY ====== */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 py-12 md:py-16" data-testid="resources-glossary-section">
        <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#7A736E] mb-2">Vocabulary</p>
            <h2 className="font-display text-3xl md:text-4xl font-semibold">
              Caregiving terms, decoded.
            </h2>
            <p className="text-[#7A736E] mt-3 max-w-2xl">
              Acronyms and jargon you&apos;ll hear from doctors, nurses, insurers, and case managers — explained the way a friend would explain them.
            </p>
          </div>
          <p className="text-sm text-[#7A736E]">{filteredGlossary.length} term{filteredGlossary.length === 1 ? "" : "s"}</p>
        </div>

        {filteredGlossary.length === 0 && (
          <p className="text-center text-[#7A736E] py-12">No terms match &quot;{query}&quot;.</p>
        )}

        <Accordion type="multiple" className="space-y-3" data-testid="resources-glossary-accordion">
          {filteredGlossary.map((item, i) => (
            <AccordionItem
              key={item.term}
              value={`g-${i}`}
              className="hc-card px-6 border-0"
              data-testid={`glossary-${item.term.toLowerCase().split(' ')[0].replace(/[^a-z]/g,'')}`}
            >
              <AccordionTrigger className="font-display text-lg text-left hover:no-underline py-4">
                {item.term}
              </AccordionTrigger>
              <AccordionContent className="text-[#3D3935] leading-relaxed text-[15px] pb-5">
                {item.def}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* ====== QUICK NAV CARDS ====== */}
      <section className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 py-12">
        <div className="grid md:grid-cols-3 gap-5">
          <Link
            to="/support"
            className="hc-card p-6 hover:border-[#3A5A4A] transition group"
            data-testid="resources-card-support"
          >
            <Heart className="w-6 h-6 text-[#C87B67] mb-3" />
            <h3 className="font-display text-lg mb-2 group-hover:text-[#3A5A4A] transition">Talk to a real person</h3>
            <p className="text-sm text-[#7A736E]">Every email goes to a real human. Ask anything — even if it&apos;s not about Hope Chest.</p>
          </Link>
          <Link
            to="/changelog"
            className="hc-card p-6 hover:border-[#3A5A4A] transition group"
            data-testid="resources-card-changelog"
          >
            <Calendar className="w-6 h-6 text-[#C87B67] mb-3" />
            <h3 className="font-display text-lg mb-2 group-hover:text-[#3A5A4A] transition">What&apos;s new</h3>
            <p className="text-sm text-[#7A736E]">See what we shipped this week and what&apos;s coming next. Built from caregiver feedback.</p>
          </Link>
          <Link
            to="/pricing"
            className="hc-card p-6 hover:border-[#3A5A4A] transition group"
            data-testid="resources-card-pricing"
          >
            <FileText className="w-6 h-6 text-[#C87B67] mb-3" />
            <h3 className="font-display text-lg mb-2 group-hover:text-[#3A5A4A] transition">See plans</h3>
            <p className="text-sm text-[#7A736E]">Free forever for one loved one. Premium when your family needs to coordinate.</p>
          </Link>
        </div>
      </section>

      {/* ====== FINAL CTA ====== */}
      <section className="relative z-10 max-w-3xl mx-auto px-6 md:px-12 pb-20 pt-6 text-center">
        <h2 className="font-display text-3xl md:text-4xl font-semibold mb-4">
          Ready to put all of this into one place?
        </h2>
        <p className="text-[#7A736E] mb-7 max-w-xl mx-auto">
          Hope Chest holds the medications, appointments, contacts, documents, and notes for the people you love. Free forever for one loved one.
        </p>
        <Link
          to="/signup"
          className="inline-flex items-center gap-2 rounded-full bg-[#3A5A4A] hover:bg-[#2C4538] text-white px-8 py-3.5 text-base font-medium transition"
          data-testid="resources-signup-cta"
        >
          Start your Hope Chest, free
          <ArrowRight className="w-4 h-4" />
        </Link>
      </section>
    </div>
  );
}
